import React, { useRef } from "react";
import { Calendar, X } from "lucide-react";

/** Shared campaign / templates page palette */
export const CAMPAIGN_THEME = {
  accent: "#eefa94",
  surface: "#e9eeea",
  accentSoft: "rgba(238, 250, 148, 0.4)",
  accentRing: "rgba(17, 24, 39, 0.12)",
};

function formatDisplayDate(value) {
  if (!value) return "";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Theme-aligned date field — styled trigger + native picker (showPicker).
 */
export default function ThemeDateInput({
  value = "",
  onChange,
  placeholder = "Select date",
  min,
  max,
  className = "",
  disabled = false,
}) {
  const inputRef = useRef(null);

  const openPicker = () => {
    if (disabled) return;
    const el = inputRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      el.showPicker();
    } else {
      el.focus();
      el.click();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`w-full flex items-center gap-2.5 pl-2 pr-9 py-1.5 text-sm font-medium bg-white border rounded-xl transition-all shadow-sm min-h-[44px] ${
          disabled
            ? "border-gray-200 bg-gray-50 cursor-not-allowed"
            : "border-gray-300 hover:border-gray-400 focus-within:border-gray-900 focus-within:ring-2 focus-within:ring-[rgba(17,24,39,0.08)]"
        }`}
      >
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors disabled:opacity-50"
          style={{ backgroundColor: disabled ? "#f3f4f6" : CAMPAIGN_THEME.surface }}
          aria-label="Open date picker"
        >
          <Calendar size={15} className="text-gray-700" />
        </button>

        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          className={`flex-1 text-left truncate py-1 disabled:cursor-not-allowed ${
            value ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {value ? formatDisplayDate(value) : placeholder}
        </button>

        <input
          ref={inputRef}
          type="date"
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>

      {value && !disabled && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors z-10"
          aria-label="Clear date"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
